import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Tiny footer that surfaces inside a Radix `ScrollArea`'s parent and
 * tells the user whether more rows are hidden below the fold. Stays
 * hidden when the content fits — zero clutter on small lists.
 *
 * Drop-in usage:
 *   <ScrollArea data-testid="my-scroll">...</ScrollArea>
 *   <ScrollHint targetTestId="my-scroll" total={items.length} />
 *
 * Refs the viewport via DOM lookup (not a forwarded ref) because Radix
 * doesn't expose its viewport ref via the ScrollArea API.
 */
const ScrollHint = ({ targetTestId, total, ...rest }) => {
  const [state, setState] = useState({ visible: false, hiddenBelow: 0 });
  const viewportRef = useRef(null);

  useEffect(() => {
    const root = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (!root) return;
    const viewport = root.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;
    viewportRef.current = viewport;

    const measure = () => {
      const ovf = viewport.scrollHeight - viewport.clientHeight;
      if (ovf <= 4) {
        setState({ visible: false, hiddenBelow: 0 });
        return;
      }
      const remaining = ovf - viewport.scrollTop;
      const ratio = viewport.scrollHeight > 0 ? remaining / viewport.scrollHeight : 0;
      const hiddenBelow = total > 0 ? Math.max(0, Math.round(total * ratio)) : 0;
      setState({ visible: remaining > 4, hiddenBelow });
    };

    measure();
    viewport.addEventListener('scroll', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    return () => {
      viewport.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [targetTestId, total]);

  if (!state.visible) return null;

  return (
    <button
      type="button"
      onClick={() => {
        const v = viewportRef.current;
        if (v) v.scrollBy({ top: v.clientHeight * 0.8, behavior: 'smooth' });
      }}
      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] text-slate-500 border-t border-slate-100 bg-gradient-to-b from-white to-slate-50 hover:from-slate-50 hover:to-slate-100 transition"
      {...rest}
    >
      <span>
        Showing top results · <strong className="text-slate-700">{state.hiddenBelow}</strong> more below
      </span>
      <ChevronDown className="w-3 h-3 animate-bounce" />
    </button>
  );
};

export default ScrollHint;
